import { ChevronDownIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { WorkspaceRole } from '@/lib/api/generated/model';

import { ACCESS_COPY } from '../accessCopy';
import {
  useAddFromRequest,
  useDismissRequest,
} from '../hooks/useAccessRequests';

/**
 * Add or Dismiss a request to join (spec 190), wherever an owner meets it: the
 * inbox's notice and Settings → People. Add makes the person a member.
 *
 * Settings also offers "Add as owner" beside Add (spec 196 US5), so a request
 * from someone who will run the workspace is not an add and then a role change.
 * The inbox keeps the plain pair: a notice is answered in passing.
 *
 * `onAnswered` hears of an answer the service accepted, before the list is read
 * again, so a list can let the row leave rather than have it vanish.
 */
export function AccessRequestActions({
  workspaceId,
  requestId,
  name,
  size = 'xs',
  withOwner = false,
  onAnswered,
}: {
  workspaceId: string;
  requestId: string;
  /** Who asked, for the toast. */
  name: string;
  size?: 'xs' | 'sm';
  /** Offer "Add as owner" in a menu beside Add. */
  withOwner?: boolean;
  onAnswered?: (requestId: string) => void;
}) {
  const add = useAddFromRequest();
  const dismiss = useDismissRequest();
  const busy = add.isPending || dismiss.isPending;

  function addAs(role: WorkspaceRole) {
    add.mutate(
      { workspaceId, requestId, role },
      {
        onSuccess: () => {
          onAnswered?.(requestId);
          toast.success(
            role === 'owner'
              ? `${name} was added as an owner.`
              : ACCESS_COPY.added(name),
          );
        },
        onError: (failure) => toast.error(failure.message),
      },
    );
  }

  return (
    <span className="flex items-center gap-1.5">
      <Button
        size={size}
        disabled={busy}
        aria-label={`${ACCESS_COPY.addRequest} ${name}`}
        onClick={() => {
          addAs('member');
        }}
      >
        {ACCESS_COPY.addRequest}
      </Button>
      {withOwner ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={busy}
            aria-label={`More ways to add ${name}`}
            render={<Button size={size} variant="outline" className="px-1.5" />}
          >
            <ChevronDownIcon aria-hidden="true" strokeWidth={1.5} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                addAs('owner');
              }}
            >
              Add as owner
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <Button
        size={size}
        variant="outline"
        disabled={busy}
        aria-label={`${ACCESS_COPY.dismissRequest} ${name}’s request`}
        onClick={() => {
          dismiss.mutate(
            { workspaceId, requestId },
            {
              onSuccess: () => {
                onAnswered?.(requestId);
                toast(ACCESS_COPY.dismissed(name));
              },
              onError: (failure) => toast.error(failure.message),
            },
          );
        }}
      >
        {ACCESS_COPY.dismissRequest}
      </Button>
    </span>
  );
}
